// src/components/PendingApprovalsView.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc, arrayUnion, Timestamp, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { getPendingApprovalsForUser, formatApprovalStatus, validateApprovalAction, APPROVAL_DECISIONS, calculateApprovalStatus, isStageComplete } from '../utils/approvalUtils';
import { getCurrentUserCompanyId, getCurrentUserData } from '../utils/userUtils';
import SignaturePad from 'react-signature-canvas';
import { useTranslation } from 'react-i18next';

export default function PendingApprovalsView() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [decision, setDecision] = useState('');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [userGroups, setUserGroups] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const signaturePadRef = useRef(null);

  useEffect(() => {
    loadPendingApprovals();
    // Refresh every 30 seconds
    const interval = setInterval(loadPendingApprovals, 30000);
    return () => clearInterval(interval);
  }, []);

  async function loadPendingApprovals() {
    try {
      const user = auth.currentUser;
      if (!user) return;

      const companyId = await getCurrentUserCompanyId();
      if (!companyId) return;

      const userData = await getCurrentUserData();
      setUserGroups(userData?.groups || []);
      setUserRoles(userData?.customRoles || []);

      // Load all submissions with their approvals
      const submissionsRef = collection(db, 'companies', companyId, 'submissions');
      const submissionsSnap = await getDocs(submissionsRef);

      console.log('Found submissions:', submissionsSnap.size);
      const submissionsWithApprovals = [];

      for (const subDoc of submissionsSnap.docs) {
        const submissionData = { id: subDoc.id, ...subDoc.data() };
        
        // Skip drafts from approval workflow
        if (submissionData.status === "draft") continue;

        // Load approvals subcollection
        const approvalsRef = collection(db, 'companies', companyId, 'submissions', subDoc.id, 'approvals');
        const approvalsSnap = await getDocs(approvalsRef);
        
        console.log(`Submission ${subDoc.id} has ${approvalsSnap.size} approvals`);
        
        submissionData.approvals = approvalsSnap.docs.map(aDoc => {
          const approval = { id: aDoc.id, ...aDoc.data() };
          console.log('Approval data:', approval);
          return approval;
        });

        // Calculate approval status
        submissionData.approvalStatus = calculateApprovalStatus(submissionData.approvals);

        submissionsWithApprovals.push(submissionData);
      }

      console.log('Current user ID:', user.uid);
      console.log('User groups:', userData?.groups);
      console.log('User roles:', userData?.customRoles);

      const pending = getPendingApprovalsForUser(
        submissionsWithApprovals,
        user.uid,
        userData?.groups || [],
        userData?.customRoles || []
      );

      console.log('Pending approvals for user:', pending);
      setPendingApprovals(pending);
    } catch (error) {
      console.error('Error loading pending approvals:', error);
    } finally {
      setLoading(false);
    }
  }

  async function openApprovalModal(approval) {
    try {
      const companyId = await getCurrentUserCompanyId();
      
      // Load full submission data
      const submissionRef = doc(db, 'companies', companyId, 'submissions', approval.submissionId);
      const submissionSnap = await getDoc(submissionRef);
      
      if (!submissionSnap.exists()) {
        alert(t('approvals.submissionNotFound'));
        return;
      }

      const fullSubmission = { id: submissionSnap.id, ...submissionSnap.data() };
      
      // Load approvals
      const approvalsRef = collection(db, 'companies', companyId, 'submissions', approval.submissionId, 'approvals');
      const approvalsSnap = await getDocs(approvalsRef);
      
      const approvalsData = approvalsSnap.docs.map(aDoc => ({
        id: aDoc.id,
        ...aDoc.data()
      }));

      const currentApproval = approvalsData.find(a => a.id === approval.approvalId);

      setSelectedApproval({
        ...approval,
        submission: fullSubmission,
        approvalData: currentApproval
      });
      
      setDecision('');
      setComment('');
      if (signaturePadRef.current) {
        signaturePadRef.current.clear();
      }
      setShowApprovalModal(true);
    } catch (error) {
      console.error('Error loading approval details:', error);
      alert(t('approvals.failedLoadApproval'));
    }
  }

  function clearSignature() {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
  }

  async function submitApproval() {
    if (!selectedApproval || !decision) {
      alert(t('approvals.pleaseSelectDecision'));
      return;
    }

    const user = auth.currentUser;
    const userData = await getCurrentUserData();

    const approvalData = {
      decision,
      comment: comment.trim(),
      signature: null
    };

    // Check if signature is required
    if (selectedApproval.requirements.signature) {
      if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
        alert(t('approvals.signatureRequiredAlert'));
        return;
      }
      approvalData.signature = signaturePadRef.current.toDataURL();
    }

    // Validate requirements
    const validation = validateApprovalAction(selectedApproval.approvalData, approvalData);
    if (!validation.valid) {
      alert(validation.errors.join('\n'));
      return;
    }

    setSubmitting(true);

    try {
      const companyId = await getCurrentUserCompanyId();
      
      const approvalRef = doc(
        db,
        'companies',
        companyId,
        'submissions',
        selectedApproval.submissionId,
        'approvals',
        selectedApproval.approvalId
      );

      const approvalAction = {
        userId: user.uid,
        userName: userData?.name || user.email,
        decision,
        comment: comment.trim(),
        signature: approvalData.signature,
        timestamp: Timestamp.now()
      };

      // Add approval to the stage's approvedBy array
      await updateDoc(approvalRef, {
        approvedBy: arrayUnion(approvalAction),
        updatedAt: Timestamp.now()
      });

      // Check if stage is now complete
      const approvalSnap = await getDoc(approvalRef);
      const updatedApprovalData = approvalSnap.data();
      
      if (isStageComplete({ ...updatedApprovalData, id: approvalSnap.id })) {
        // Mark stage as approved/rejected based on decision
        const newStatus = decision === APPROVAL_DECISIONS.APPROVE ? 'approved' : 
                         decision === APPROVAL_DECISIONS.REJECT ? 'rejected' :
                         'changes-requested';
        
        await updateDoc(approvalRef, {
          status: newStatus,
          updatedAt: Timestamp.now()
        });

        // Update submission's overall approval status
        const approvalsRef = collection(db, 'companies', companyId, 'submissions', selectedApproval.submissionId, 'approvals');
        const approvalsSnap = await getDocs(approvalsRef);
        const allApprovals = approvalsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        const overallStatus = calculateApprovalStatus(allApprovals);
        
        const submissionRef = doc(db, 'companies', companyId, 'submissions', selectedApproval.submissionId);
        
        // Prepare submission update
        const submissionUpdate = {
          approvalStatus: overallStatus,
          updatedAt: Timestamp.now()
        };
        
        // If changes are requested, allow the conductor to edit
        if (newStatus === 'changes-requested') {
          submissionUpdate.status = 'draft'; // Set back to draft so conductor can edit
          submissionUpdate.changesRequested = true;
          submissionUpdate.changesRequestedAt = Timestamp.now();
          submissionUpdate.changesRequestedBy = userData?.name || user.email;
          submissionUpdate.changesRequestedComment = comment.trim();
        }
        
        await updateDoc(submissionRef, submissionUpdate);
      }

      alert(t('approvals.submitSuccess'));
      setShowApprovalModal(false);
      setSelectedApproval(null);
      loadPendingApprovals();
    } catch (error) {
      console.error('Error submitting approval:', error);
      alert(t('approvals.failedSubmitApproval', { error: error.message }));
    } finally {
      setSubmitting(false);
    }
  }

  const shell = (content) => (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black text-gray-50 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -left-24 -top-24 w-72 h-72 bg-red-600/25 blur-3xl rounded-full" />
        <div className="absolute right-10 top-10 w-96 h-96 bg-rose-500/20 blur-3xl rounded-full" />
        <div className="absolute -right-24 bottom-10 w-80 h-80 bg-amber-400/12 blur-3xl rounded-full" />
      </div>
      <div className="relative z-10 max-w-6xl mx-auto px-6 py-10 lg:py-14">
        {content}
      </div>
    </div>
  );

  if (loading) {
    return shell(
      <div className="flex flex-col items-center justify-center py-12">
        <div className="h-12 w-12 border-2 border-rose-400/60 border-t-transparent rounded-full animate-spin mb-4" />
        <div className="text-gray-200 text-lg">{t('approvals.loadingApprovals')}</div>
      </div>
    );
  }

  if (pendingApprovals.length === 0) {
    return shell(
      <div>
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-gray-200 hover:text-white transition"
          >
            <span className="text-lg">←</span>
            {t('common.back')}
          </button>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 rounded-lg bg-white/10 border border-white/15 text-gray-100 hover:bg-white/15 transition"
          >
            🏠 {t('common.home')}
          </button>
        </div>

        <div className="text-center bg-white/5 border border-white/10 rounded-3xl p-10 backdrop-blur-lg shadow-2xl">
          <div className="text-5xl mb-4">✅</div>
          <h3 className="text-2xl font-semibold text-white mb-2">{t('approvals.noPendingApprovals')}</h3>
          <p className="text-sm text-gray-300 mb-2">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
          <p className="text-sm text-gray-400 max-w-xl mx-auto">
            {t('approvals.noPendingDescription')}
          </p>
        </div>
      </div>
    );
  }

  return shell(
    <div className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-gray-400">{t('approvals.approvalsTitle')}</p>
          <h2 className="text-3xl font-bold text-white">{t('approvals.pendingApprovalsCount', { count: pendingApprovals.length })}</h2>
          <p className="text-sm text-gray-300">{t('approvals.pendingDescription')}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 rounded-lg bg-white/10 border border-white/15 text-gray-100 hover:bg-white/15 transition"
          >
            ← {t('common.back')}
          </button>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white shadow-lg shadow-rose-900/30 transition"
          >
            🏠 {t('common.home')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {pendingApprovals.map((approval) => {
          const completedDate = approval.completedAt?.toDate();
          const daysAgo = completedDate ? Math.floor((new Date() - completedDate) / (1000 * 60 * 60 * 24)) : 0;

          return (
            <div
              key={`${approval.submissionId}-${approval.approvalId}`}
              className="bg-white/5 border border-white/10 rounded-2xl p-5 backdrop-blur-lg shadow-xl hover:-translate-y-0.5 transition transform"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="font-semibold text-white mb-1">{approval.submissionTitle}</h3>
                  <div className="text-sm text-gray-200 space-y-1">
                    {approval.documentNumber && <div>📄 {approval.documentNumber}</div>}
                    <div>👤 {approval.conductedByName}</div>
                    <div>📅 {completedDate?.toLocaleDateString()} ({daysAgo === 0 ? t('approvals.today') : t('approvals.daysAgo', { count: daysAgo })})</div>
                  </div>
                </div>
                <div className="px-3 py-1 bg-amber-400/15 text-amber-200 text-xs font-bold rounded-full border border-amber-200/30">
                  {t('approvals.stageNumber', { number: approval.stageNumber })}
                </div>
              </div>

              <div className="mb-4 p-3 bg-sky-500/10 border border-sky-400/30 rounded-xl">
                <div className="text-sm font-semibold text-sky-100 mb-1">{approval.stageName}</div>
                <div className="text-xs text-sky-200 space-y-1">
                  {approval.requirements.signature && <div>✍️ {t('approvals.signatureRequired')}</div>}
                  {approval.requirements.comment && <div>💬 {t('approvals.commentRequired')}</div>}
                  {approval.requirements.decision && <div>✅ {t('approvals.decisionRequired')}</div>}
                </div>
              </div>

              <button
                onClick={() => openApprovalModal(approval)}
                className="w-full px-4 py-3 bg-gradient-to-r from-rose-600 to-amber-500 hover:from-rose-500 hover:to-amber-400 text-white rounded-xl font-semibold shadow-lg shadow-rose-900/30 transition"
              >
                {t('approvals.reviewAndApprove')}
              </button>
            </div>
          );
        })}
      </div>

      {/* Approval Modal */}
      {showApprovalModal && selectedApproval && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gray-900 text-gray-50 border border-white/10 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-gray-400">{t('approvals.approvalsTitle')}</p>
                  <h3 className="text-2xl font-bold">{t('approvals.reviewCheck', { stageName: selectedApproval.stageName })}</h3>
                </div>
                <button
                  onClick={() => { setShowApprovalModal(false); setSelectedApproval(null); }}
                  className="text-sm text-gray-300 hover:text-white"
                  disabled={submitting}
                >
                  ✕ {t('common.close')}
                </button>
              </div>

              {/* Submission Details */}
              <div className="p-4 bg-white/5 border border-white/10 rounded-xl space-y-3">
                <h4 className="font-semibold text-white">{t('approvals.checkDetails')}</h4>
                <div className="grid grid-cols-2 gap-4 text-sm text-gray-200">
                  <div>
                    <div className="text-gray-400">{t('approvals.form')}</div>
                    <div className="font-medium">{selectedApproval.submissionTitle}</div>
                  </div>
                  <div>
                    <div className="text-gray-400">{t('approvals.documentNumber')}</div>
                    <div className="font-medium">{selectedApproval.documentNumber || t('common.na')}</div>
                  </div>
                  <div>
                    <div className="text-gray-400">{t('approvals.conductedBy')}</div>
                    <div className="font-medium">{selectedApproval.conductedByName}</div>
                  </div>
                  <div>
                    <div className="text-gray-400">{t('approvals.completed')}</div>
                    <div className="font-medium">{selectedApproval.completedAt?.toDate().toLocaleString()}</div>
                  </div>
                </div>

                {selectedApproval.submission?.responses && (
                  <div>
                    <h5 className="font-semibold text-white mb-2">{t('approvals.responsesFirst', { count: 10 })}</h5>
                    <div className="max-h-96 overflow-y-auto border border-white/10 rounded p-3 bg-black/30 space-y-3">
                      {Object.entries(selectedApproval.submission.responses).slice(0, 10).map(([key, value]) => {
                        let displayLabel = '';
                        let displayValue = '';
                        let isImage = false;
                        try {
                          let data = value;
                          if (typeof value === 'string') {
                            try { data = JSON.parse(value); } catch (e) { displayLabel = key; displayValue = value === '' ? t('approvals.empty') : value; }
                          }
                          if (typeof data === 'object' && data !== null) {
                            displayLabel = data.label || key;
                            const val = data.value;
                            if (typeof val === 'boolean') displayValue = val ? t('common.yes') : t('common.no');
                            else if (typeof val === 'string') {
                              if (val.startsWith('data:image')) { isImage = true; displayValue = val; }
                              else if (val === '') displayValue = t('approvals.empty');
                              else if (val.startsWith('http')) displayValue = t('approvals.fileUrl');
                              else displayValue = val;
                            } else if (typeof val === 'object') {
                              if (val?.title) displayValue = `${val.title}${val.role ? ` (${val.role})` : ''}`; else displayValue = t('approvals.complexObject');
                            } else displayValue = String(val);
                          }
                        } catch (e) {
                          displayLabel = key; displayValue = t('approvals.errorParsingResponse');
                        }
                        return (
                          <div key={key} className="pb-3 border-b border-white/5 last:border-0">
                            <div className="text-xs font-medium text-gray-400 mb-1">{displayLabel}</div>
                            {isImage ? (
                              <img src={displayValue} alt={displayLabel} className="max-w-xs max-h-40 border border-white/10 rounded" />
                            ) : (
                              <div className="text-sm text-gray-100">{displayValue}</div>
                            )}
                          </div>
                        );
                      })}
                      {Object.keys(selectedApproval.submission.responses).length > 10 && (
                        <div className="text-xs text-gray-400 italic">
                          {t('approvals.moreResponses', { count: Object.keys(selectedApproval.submission.responses).length - 10 })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Decision */}
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-gray-200">{t('approvals.decisionLabel')} *</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <button
                    onClick={() => setDecision(APPROVAL_DECISIONS.APPROVE)}
                    className={`px-4 py-3 rounded-lg font-semibold transition-all ${decision === APPROVAL_DECISIONS.APPROVE ? 'bg-emerald-500 text-white shadow-lg' : 'bg-white/5 border border-white/10 text-gray-100'}`}
                  >
                    ✅ {t('approvals.approve')}
                  </button>
                  <button
                    onClick={() => setDecision(APPROVAL_DECISIONS.REJECT)}
                    className={`px-4 py-3 rounded-lg font-semibold transition-all ${decision === APPROVAL_DECISIONS.REJECT ? 'bg-red-500 text-white shadow-lg' : 'bg-white/5 border border-white/10 text-gray-100'}`}
                  >
                    ❌ {t('approvals.reject')}
                  </button>
                  <button
                    onClick={() => setDecision(APPROVAL_DECISIONS.REQUEST_CHANGES)}
                    className={`px-4 py-3 rounded-lg font-semibold transition-all ${decision === APPROVAL_DECISIONS.REQUEST_CHANGES ? 'bg-amber-500 text-white shadow-lg' : 'bg-white/5 border border-white/10 text-gray-100'}`}
                  >
                    🔄 {t('approvals.requestChanges')}
                  </button>
                </div>
              </div>

              {/* Comment */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-200">
                  {t('approvals.commentLabel')} {selectedApproval.requirements.comment && '*'}
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-white/10 rounded-lg bg-white/5 text-gray-50 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-rose-500"
                  placeholder={t('approvals.commentPlaceholder')}
                />
              </div>

              {/* Signature */}
              {selectedApproval.requirements.signature && (
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-200">{t('approvals.signatureLabel')} *</label>
                  <div className="border-2 border-dashed border-white/20 rounded-lg overflow-hidden bg-black/30">
                    <SignaturePad
                      ref={signaturePadRef}
                      canvasProps={{ className: 'w-full h-40', style: { touchAction: 'none' } }}
                    />
                  </div>
                  <button
                    onClick={clearSignature}
                    className="text-sm text-sky-300 hover:text-white"
                  >
                    {t('approvals.clearSignature')}
                  </button>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => { setShowApprovalModal(false); setSelectedApproval(null); }}
                  className="px-6 py-3 bg-white/5 border border-white/10 text-gray-100 rounded-lg hover:bg-white/10 transition"
                  disabled={submitting}
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={submitApproval}
                  disabled={submitting || !decision}
                  className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-rose-500 text-white rounded-lg shadow-lg hover:from-emerald-400 hover:to-rose-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? t('approvals.submitting') : t('approvals.submitApproval')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
